/**
 * TeachBud Grade-Band Layout System
 * ──────────────────────────────────────────────────────────────────────────────
 * Encodes the automatic layout decisions from the Curriculum Rendering Spec v3.1
 * (docs/curriculum-rendering-spec.md, Sections 1–2).
 *
 * The renderer never asks teachers to specify obvious formatting needs.
 * Grade band is the first-tier layout signal — before subject, before concept.
 *
 * Usage:
 *   const band = getGradeBandConfig(sheet.grade);          // sheet.grade is 0=K, 1-8
 *   const prefs = applyGradeBandDefaults(DEFAULT_FORMAT, sheet.grade);
 */

// ── Grade-band config type ────────────────────────────────────────────────────

/** Subset of FormatPreferences fields that grade-band defaults control. */
export interface GradeBandLayoutOverrides {
  fontSize: 13 | 15 | 17 | 19;
  lineSpacing: 'compact' | 'normal' | 'relaxed';
  workspaceSize: 'none' | 'small' | 'medium' | 'large' | 'xl';
  answerLinesCount: number;
  responseBoxHeight: number;
}

export interface GradeBandConfig {
  /** Grade number: 0 = Kindergarten, 1–8 = Grades 1–8, 9–12 = high school */
  gradeNum: number;

  /**
   * Body font size — nearest value in our 13|15|17|19 type union.
   * Spec values: K=20, G1=18, G2=16, G3=14, G4=13, G5=12, G6-8=11, G9-12=11.
   */
  fontSize: 13 | 15 | 17 | 19;

  /**
   * Line spacing token.
   * Spec: K-1 = 2.0, G2-3 = 1.8, G4-8 = 1.6.
   * Maps to: relaxed=2.0 (K-1) | relaxed=1.8 (2-3) | normal=1.6 (4-8).
   */
  lineSpacing: 'compact' | 'normal' | 'relaxed';

  /** CSS line-height value — spec-aligned, more precise than the token alone. */
  lineHeight: string;

  /** Default workspace size for this grade band. */
  workspaceSize: 'small' | 'medium' | 'large' | 'xl';

  /** Minimum ruled answer lines per problem. */
  answerLinesCount: number;

  /** Minimum computation/response box height in px. */
  responseBoxHeight: number;

  /** Spec maximum words per activity sheet page. */
  maxWordsPerPage: number;

  /**
   * Visual-dominant layout: workspace SVG renders BEFORE problem text.
   * True for K-1 (70-60% image ratio). False for Grades 2+.
   * Spec §1.1: visual is the primary anchor; text is secondary.
   */
  visualDominant: boolean;

  /**
   * Workspace border weight in px.
   * Spec §1.5: K-2 dashed borders use 2px stroke; 3+ use 1px.
   */
  workspaceBorderPx: 1 | 2;

  /**
   * Image-to-text ratio target. Decimal: 0.70 = 70% visual, 30% text.
   * Used by layout decisions that choose between compact and spacious modes.
   */
  imageRatio: number;

  /** Maximum items (problems) per page for this grade band. */
  maxItemsPerPage: number;

  /** Heading font size offset above body (px). Spec: heading ≥ body + 4. */
  headingOffset: number;
}

// ── Per-grade configs ─────────────────────────────────────────────────────────

const GRADE_CONFIGS: Record<number, GradeBandConfig> = {
  0: {
    // Kindergarten — the design benchmark. Maximum visual, minimum text.
    gradeNum: 0,
    fontSize: 19,
    lineSpacing: 'relaxed',
    lineHeight: '2.0',
    workspaceSize: 'xl',
    answerLinesCount: 2,
    responseBoxHeight: 200,   // spec: min 2.5 in ≈ 240 px; 200 for practical height
    maxWordsPerPage: 80,
    visualDominant: true,
    workspaceBorderPx: 2,
    imageRatio: 0.70,
    maxItemsPerPage: 3,
    headingOffset: 6,
  },
  1: {
    // Grade 1
    gradeNum: 1,
    fontSize: 19,
    lineSpacing: 'relaxed',
    lineHeight: '2.0',
    workspaceSize: 'xl',
    answerLinesCount: 3,
    responseBoxHeight: 168,   // spec: min 2.0 in
    maxWordsPerPage: 80,
    visualDominant: true,
    workspaceBorderPx: 2,
    imageRatio: 0.60,
    maxItemsPerPage: 4,
    headingOffset: 5,
  },
  2: {
    // Grade 2
    gradeNum: 2,
    fontSize: 17,
    lineSpacing: 'relaxed',
    lineHeight: '1.8',
    workspaceSize: 'large',
    answerLinesCount: 4,
    responseBoxHeight: 144,
    maxWordsPerPage: 150,
    visualDominant: false,
    workspaceBorderPx: 2,
    imageRatio: 0.50,
    maxItemsPerPage: 5,
    headingOffset: 5,
  },
  3: {
    // Grade 3
    gradeNum: 3,
    fontSize: 15,
    lineSpacing: 'relaxed',
    lineHeight: '1.8',
    workspaceSize: 'large',
    answerLinesCount: 4,
    responseBoxHeight: 130,
    maxWordsPerPage: 150,
    visualDominant: false,
    workspaceBorderPx: 1,
    imageRatio: 0.45,
    maxItemsPerPage: 6,
    headingOffset: 4,
  },
  4: {
    // Grade 4
    gradeNum: 4,
    fontSize: 13,
    lineSpacing: 'normal',
    lineHeight: '1.7',
    workspaceSize: 'large',
    answerLinesCount: 5,
    responseBoxHeight: 120,
    maxWordsPerPage: 250,
    visualDominant: false,
    workspaceBorderPx: 1,
    imageRatio: 0.40,
    maxItemsPerPage: 7,
    headingOffset: 4,
  },
  5: {
    // Grade 5
    gradeNum: 5,
    fontSize: 13,
    lineSpacing: 'normal',
    lineHeight: '1.65',
    workspaceSize: 'large',
    answerLinesCount: 5,
    responseBoxHeight: 112,
    maxWordsPerPage: 250,
    visualDominant: false,
    workspaceBorderPx: 1,
    imageRatio: 0.38,
    maxItemsPerPage: 8,
    headingOffset: 4,
  },
};

const GRADE_6_8_CONFIG: GradeBandConfig = {
  gradeNum: 6,
  fontSize: 13,
  lineSpacing: 'normal',
  lineHeight: '1.6',
  workspaceSize: 'medium',
  answerLinesCount: 5,
  responseBoxHeight: 96,
  maxWordsPerPage: 350,
  visualDominant: false,
  workspaceBorderPx: 1,
  imageRatio: 0.30,
  maxItemsPerPage: 8,
  headingOffset: 4,
};

const GRADE_9_12_CONFIG: GradeBandConfig = {
  gradeNum: 9,
  fontSize: 13,
  lineSpacing: 'normal',
  lineHeight: '1.5',
  workspaceSize: 'medium',
  answerLinesCount: 5,
  responseBoxHeight: 80,
  maxWordsPerPage: 450,
  visualDominant: false,
  workspaceBorderPx: 1,
  imageRatio: 0.25,
  maxItemsPerPage: 10,
  headingOffset: 4,
};

// ── Public API ────────────────────────────────────────────────────────────────

/**
 * Returns the GradeBandConfig for a given grade number.
 * gradeNum: 0 = Kindergarten, 1–8, 9–12.
 * Defaults to Grade 4 config for unrecognised values.
 */
export function getGradeBandConfig(gradeNum: number): GradeBandConfig {
  if (gradeNum >= 9) return { ...GRADE_9_12_CONFIG, gradeNum };
  if (gradeNum >= 6) return { ...GRADE_6_8_CONFIG, gradeNum };
  return GRADE_CONFIGS[gradeNum] ?? GRADE_CONFIGS[4];
}

/**
 * Parses a grade label string → grade number.
 * Handles "Kindergarten", "K", "Grade 3", "3", "Grade K", "0" → 0-12.
 */
export function gradeNumFromLabel(gradeLabel: string): number {
  const s = (gradeLabel ?? '').toLowerCase().trim();
  if (s === 'kindergarten' || s === 'k' || s === 'grade k' || s === 'grade 0' || s === '0') return 0;
  const m = s.match(/(\d+)/);
  return m ? parseInt(m[1], 10) : 4;
}

/**
 * Returns the grade-band layout overrides for a given grade.
 * Callers spread these over their FormatPreferences object.
 *
 *   const prefs = { ...DEFAULT_FORMAT, ...getGradeBandOverrides(sheet.grade) };
 *
 * Called automatically when a new sheet is generated or loaded.
 */
export function getGradeBandOverrides(gradeNum: number): GradeBandLayoutOverrides {
  const band = getGradeBandConfig(gradeNum);
  return {
    fontSize: band.fontSize,
    lineSpacing: band.lineSpacing,
    workspaceSize: band.workspaceSize,
    answerLinesCount: band.answerLinesCount,
    responseBoxHeight: band.responseBoxHeight,
  };
}
